import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Paper,
    Typography,
    Grid,
    TextField,
    Button,
    MenuItem,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    CircularProgress,
    Snackbar,
    Alert,
    Pagination,
    FormControlLabel,
    Checkbox,
    FormControl,
    InputLabel,
    Select,
    ListItemText,
} from "@mui/material";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddCircleIcon from "@mui/icons-material/AddCircle";


import InventoryServices from "../../services/InventoryServices";

const CommercialActivation = () => {
    const navigate = useNavigate();
    /* ----------------------------------
        Upload Result
    ---------------------------------- */

    const [uploadFile, setUploadFile] = useState(null);
    const [uploadDurationDays, setUploadDurationDays] = useState(null);
    /* ----------------------------------
        Create Request
    ---------------------------------- */

    const [stockIds, setStockIds] = useState("");

    const [period, setPeriod] = useState("1_year");

    /* ----------------------------------
        Search Filters
    ---------------------------------- */

    const [search, setSearch] = useState("");

    const [source, setSource] = useState("");
    const [periodFilter, setPeriodFilter] = useState("");

    /* ----------------------------------
        Table
    ---------------------------------- */

    const [rows, setRows] = useState([]);

    const [page, setPage] = useState(1);

    const [pageSize] = useState(50);

    const [totalPages, setTotalPages] = useState(1);

    const [loading, setLoading] = useState(false);
    /* ----------------------------------
          M2M Frontend Approval Validation
      ---------------------------------- */

    const [durationChecked, setDurationChecked] = useState(false);

    const [whitelistedIpUrlChecked, setWhitelistedIpUrlChecked] =
        useState(false);

    const [selectedIpUrls, setSelectedIpUrls] = useState([]);

    const [whitelistedMobileChecked, setWhitelistedMobileChecked] =
        useState(false);

    const [selectedMobileNos, setSelectedMobileNos] = useState([]);
    /* ----------------------------------
        Snackbar
    ---------------------------------- */

    const [snackbar, setSnackbar] = useState({
        open: false,
        severity: "success",
        message: "",
    });

    const showSnackbar = (message, severity = "success") => {
        setSnackbar({
            open: true,
            severity,
            message,
        });
    };

    const whitelistedIpUrls = [
        "4.213.225.168",
        "135.235.166.209",
        "20.244.106.211",
        "103.195.217.127",
        "vltsemg.rajasthan.gov.in",
        "track.rajvts.com",
        "vlts.rajasthan.gov.in",
        "commonlayer.parivahan.gov.in",
        "vltspvt.rajasthan.gov.in",
    ];

    const whitelistedMobileNumbers = [
        "7635975659",
        "9211252018",
        "9667799546",
    ];

    /* ============================================
            M2M Approval Validation
        ============================================= */

    /*
        Approve is enabled ONLY when:

        1. Duration OK is checked
        2. Whitelisting OK is checked

        This is currently frontend-only validation.
    */

    /* ============================================
    M2M Frontend Upload Validation
============================================= */
    // const canUpload =
    //     durationChecked === true &&
    //     whitelistedIpUrlChecked === true &&
    //     selectedIpUrls.length > 0 &&
    //     whitelistedMobileChecked === true &&
    //     selectedMobileNos.length > 0;

    const canUpload =
        durationChecked === true &&
        whitelistedIpUrlChecked === true &&
        whitelistedMobileChecked === true;

    /* ----------------------------------
        Load List
    ---------------------------------- */

    useEffect(() => {
        getCommercialList();
    }, [page]);

    /* ----------------------------------
        Table Columns
    ---------------------------------- */

    const columns = [
        "id",
        "uid",
        "imei",
        "commercial_request_source",
        "commercial_activation_period",
        "commercial_activation_status",
        "start_date",
        "end_date",
    ];
    /* ============================================
        Get Commercial Activation List
    ============================================= */

    const getCommercialList = async () => {
        try {
            setLoading(true);

            const payload = {
                page,
                page_size: pageSize,
            };

            if (search.trim()) {
                payload.search = search.trim();
            }

            if (source) {
                payload.source = source;
            }

            const response = await InventoryServices.commercialList(payload);

            console.log("Commercial List:", response.data);

            const result = response.data;

            // Supports multiple API response formats
            const list =
                result.results ||
                result.data ||
                result.rows ||
                [];

            setRows(list);

            const total =
                result.total_pages ||
                Math.ceil(
                    (result.count || list.length || 0) / pageSize
                ) ||
                1;

            setTotalPages(total);
        } catch (error) {
            console.error(error);

            showSnackbar(
                error?.response?.data?.message ||
                "Failed to load commercial activation requests.",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    /* ============================================
        Create Commercial Request
    ============================================= */

    const handleCreateRequest = async () => {
        if (!stockIds.trim()) {
            showSnackbar(
                "Please enter StockMaster IDs.",
                "warning"
            );
            return;
        }

        try {
            setLoading(true);

            const ids = stockIds
                .split(",")
                .map((id) => Number(id.trim()))
                .filter((id) => !isNaN(id));

            const payload = {
                ids,
                period,
            };

            console.log(payload);

            await InventoryServices.commercialCreate(payload);

            showSnackbar(
                "Commercial request created successfully."
            );

            setStockIds("");

            getCommercialList();
        } catch (error) {
            console.error(error);

            showSnackbar(
                error?.response?.data?.message ||
                "Unable to create request.",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];

        if (!file) {
            setUploadFile(null);
            setUploadDurationDays(null);
            return;
        }

        const fileName = file.name.toLowerCase();

        let durationDays = null;

        if (fileName.startsWith("1_year")) {
            durationDays = 365 + 10;
        } else if (fileName.startsWith("2_year")) {
            durationDays = 730 + 10;
        }

        setUploadFile(file);
        setUploadDurationDays(durationDays);

        // Reset Duration checkbox whenever a new file is selected
        setDurationChecked(false);
    };

    const getM2MValidityDate = (durationDays) => {
        if (!durationDays) return null;

        const date = new Date();

        date.setDate(date.getDate() + durationDays);

        return date.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "long",
            year: "numeric",
        });
    };

    /* ============================================
        Upload Result Excel
    ============================================= */

    const handleUpload = async () => {
        // if (
        //     !durationChecked ||
        //     !whitelistedIpUrlChecked ||
        //     !selectedIpUrls ||
        //     !whitelistedMobileChecked ||
        //     !selectedMobileNos
        // ) {
        //     showSnackbar(
        //         "Please complete all M2M verification checks before uploading the file.",
        //         "warning"
        //     );
        //     return;
        // }


        if (
            !durationChecked ||
            !whitelistedIpUrlChecked ||
            !whitelistedMobileChecked
        ) {
            showSnackbar(
                "Please complete all M2M verification checks before uploading the file.",
                "warning"
            );
            return;
        }

        if (!uploadFile) {
            showSnackbar(
                "Please choose an Excel file.",
                "warning"
            );
            return;
        }

        try {
            setLoading(true);

            const formData = new FormData();

            formData.append("excel_file", uploadFile);

            await InventoryServices.uploadCommercialResult(
                formData
            );

            showSnackbar(
                "Commercial activation result uploaded successfully."
            );

            setUploadFile(null);
            setDurationChecked(false);

            getCommercialList();
        } catch (error) {
            console.error(error);

            showSnackbar(
                error?.response?.data?.message ||
                "Upload failed.",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    /* ============================================
        Export Excel
    ============================================= */

    const handleExport = async () => {
        try {
            const response =
                await InventoryServices.commercialExport({});

            const blob = new Blob([response.data], {
                type: response.headers["content-type"],
            });

            const url =
                window.URL.createObjectURL(blob);

            const link =
                document.createElement("a");

            link.href = url;

            // Filename based on currently selected frontend filter
            let fileName = "all_commercial_activation.xlsx";

            if (periodFilter === "1_year") {
                fileName = "1_year_commercial_activation.xlsx";
            } else if (periodFilter === "2_year") {
                fileName = "2_year_commercial_activation.xlsx";
            }

            link.download =
                fileName;

            document.body.appendChild(link);

            link.click();

            document.body.removeChild(link);

            window.URL.revokeObjectURL(url);

            showSnackbar(
                "Export completed successfully."
            );
        } catch (error) {
            console.error(error);

            showSnackbar(
                "Export failed.",
                "error"
            );
        }
    };

    // Frontend-only filter
    const filteredRows = periodFilter
        ? rows.filter(
            (row) =>
                row.commercial_activation_period === periodFilter
        )
        : rows;

    return (
        <Box p={3}>

            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
            >
                <Typography
                    variant="h4"
                    fontWeight={700}
                >
                    Commercial Activation
                </Typography>

                <Button
                    variant="contained"
                    startIcon={<AddCircleIcon />}
                    onClick={() => navigate("/commercial-request")}
                >
                    Create Request
                </Button>
            </Box>

            {/* =======================================================
                        Upload Activation Result
            ======================================================= */}

            {/* ===================================================
        Upload Activation Result
=================================================== */}

            <Paper
                elevation={2}
                sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: 2,
                }}
            >
                <Typography
                    variant="h6"
                    fontWeight={700}
                    color="primary"
                    mb={2}
                >
                    Upload activation result file
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    mb={3}
                >
                    Upload the 17-column commercial activation result file.
                    Rows having SIM_STATUS = ACTIVATED will automatically
                    update the activation status.
                </Typography>

                {/* ================= Upload Section ================= */}

                <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    sx={{ mb: 3 }}
                >
                    <Grid item xs={12} md={5}>
                        <Button
                            component="label"
                            variant="outlined"
                            startIcon={<UploadFileIcon />}
                            fullWidth
                        >
                            {uploadFile
                                ? uploadFile.name
                                : "Choose Result File"}

                            <input
                                hidden
                                type="file"
                                accept=".xlsx,.xls,.csv"
                                onChange={handleFileChange}
                            />
                        </Button>
                    </Grid>

                    <Grid item xs={12} md="auto">
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{
                                minWidth: 150,
                            }}
                            onClick={handleUpload}
                            disabled={!uploadFile || !canUpload}
                        >
                            Upload
                        </Button>
                    </Grid>
                </Grid>

                {/* ===================================================
                    M2M Verification
    =================================================== */}

                <Paper
                    variant="outlined"
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: "background.default",
                    }}
                >
                    {/* <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        mb={1}
                    >
                        M2M Verification
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        mb={2}
                    >
                        All checks must be completed before the file can be uploaded.
                    </Typography> */}

                    {/* ================= M2M Verification Checks ================= */}

                    <Grid
                        container
                        spacing={3}
                        alignItems="flex-start"
                    >
                        {/* ================= Duration ================= */}

                        <Grid item xs={12} md={4}>
                            <Box>
                                {/* Duration Checkbox */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        minHeight: 40,
                                    }}
                                >
                                    <FormControlLabel
                                        sx={{ m: 0 }}
                                        control={
                                            <Checkbox
                                                checked={durationChecked}
                                                disabled={!uploadFile}
                                                onChange={(e) =>
                                                    setDurationChecked(e.target.checked)
                                                }
                                                color="success"
                                            />
                                        }
                                        label={
                                            <Typography fontWeight={600}>
                                                Duration OK
                                            </Typography>
                                        }
                                    />
                                </Box>

                                {/* M2M Validity Message - BELOW Duration OK */}
                                {durationChecked && (
                                    <Box
                                        sx={{
                                            mt: 1,
                                            p: 1.5,
                                            borderRadius: 1,
                                            backgroundColor: uploadDurationDays
                                                ? "success.light"
                                                : "warning.light",
                                            width: "fit-content",
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            fontWeight={700}
                                        >
                                            {uploadDurationDays
                                                ? `M2M Validity until: ${getM2MValidityDate(
                                                    uploadDurationDays
                                                )}`
                                                : "Please Upload Files."}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Grid>

                        {/* ================= Whitelisted IP / URL ================= */}

                        <Grid item xs={12} md={4}>
                            <Box>
                                {/* One Checkbox Only */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        minHeight: 40,
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={whitelistedIpUrlChecked}
                                                onChange={(e) =>
                                                    setWhitelistedIpUrlChecked(
                                                        e.target.checked
                                                    )
                                                }
                                                color="success"
                                            />
                                        }
                                        label={
                                            <Typography fontWeight={600}>
                                                Whitelisted IP/URL OK
                                            </Typography>
                                        }
                                    />
                                </Box>

                                {/* Display Complete IP / URL List */}
                                <Box
                                    sx={{
                                        mt: 1,
                                        p: 1.5,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 1,
                                        backgroundColor: "background.paper",
                                    }}
                                >
                                    {whitelistedIpUrls.map((item) => (
                                        <Typography
                                            key={item}
                                            variant="body2"
                                            sx={{
                                                mb: 0.7,
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {item}
                                        </Typography>
                                    ))}
                                </Box>
                            </Box>
                        </Grid>

                        {/* ================= Whitelisted Mobile ================= */}

                        <Grid item xs={12} md={4}>
                            <Box>
                                {/* One Checkbox Only */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        minHeight: 40,
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={whitelistedMobileChecked}
                                                onChange={(e) =>
                                                    setWhitelistedMobileChecked(
                                                        e.target.checked
                                                    )
                                                }
                                                color="success"
                                            />
                                        }
                                        label={
                                            <Typography fontWeight={600}>
                                                Whitelisted Mobile No. OK
                                            </Typography>
                                        }
                                    />
                                </Box>

                                {/* Display Mobile Number List */}
                                <Box
                                    sx={{
                                        mt: 1,
                                        p: 1.5,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 1,
                                        backgroundColor: "background.paper",
                                    }}
                                >
                                    {whitelistedMobileNumbers.map((number) => (
                                        <Typography
                                            key={number}
                                            variant="body2"
                                            sx={{
                                                mb: 0.5,
                                            }}
                                        >
                                            {number}
                                        </Typography>
                                    ))}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>

                    {/* ================= Validation Message ================= */}

                    {/* <Box
            mt={2}
            sx={{
                p: 1.5,
                borderRadius: 1,
                backgroundColor: canUpload
                    ? "success.light"
                    : "action.hover",
            }}
        >
            <Typography
                variant="body2"
                fontWeight={600}
            >
                {canUpload
                    ? "✓ Verification completed. You can now upload the file."
                    : "Upload is disabled until all M2M verification checks are completed."}
            </Typography>
        </Box> */}
                </Paper>

            </Paper>
            {/* =======================================================
                        Search / Filter
            ======================================================= */}

            <Paper
                elevation={2}
                sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: 2,
                }}
            >
                <Typography
                    variant="h6"
                    fontWeight={700}
                    color="primary"
                    mb={2}
                >
                    Search / Filter
                </Typography>

                <Grid container spacing={2} alignItems="center">

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search uid / imei / iccid / msisdn / basket..."
                            value={search}
                            onChange={(e) =>
                                setSearch(e.target.value)
                            }
                        />
                    </Grid>

                    <Grid item xs={12} md={2}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            value={source}
                            onChange={(e) =>
                                setSource(e.target.value)
                            }
                            SelectProps={{
                                displayEmpty: true,
                            }}
                        >
                            <MenuItem value="">
                                -- source: any --
                            </MenuItem>

                            <MenuItem value="self">
                                Self
                            </MenuItem>

                            <MenuItem value="dealer">
                                Dealer
                            </MenuItem>

                            <MenuItem value="esimmanager">
                                esimmanager
                            </MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            value={periodFilter}
                            onChange={(e) => setPeriodFilter(e.target.value)}
                            SelectProps={{
                                displayEmpty: true,
                            }}
                        >
                            <MenuItem value="">
                                -- period: any --
                            </MenuItem>

                            <MenuItem value="1_year">
                                1 Year
                            </MenuItem>

                            <MenuItem value="2_year">
                                2 Year
                            </MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item>
                        <Button
                            variant="contained"
                            startIcon={<SearchIcon />}
                            onClick={() => {
                                setPage(1);
                                getCommercialList();
                            }}
                        >
                            Search
                        </Button>
                    </Grid>

                    <Grid item>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setSearch("");
                                setSource("");
                                setPeriodFilter("");
                                setPage(1);
                            }}
                        >
                            Reset
                        </Button>
                    </Grid>

                    <Grid item>
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<FileDownloadIcon />}
                            onClick={handleExport}
                        >
                            Export Excel
                        </Button>
                    </Grid>

                </Grid>

            </Paper>

            {/* =======================================================
                        Commercial Activation Requests
            ======================================================= */}

            <Paper
                elevation={2}
                sx={{
                    p: 3,
                    borderRadius: 2,
                }}
            >
                <Typography
                    variant="h6"
                    fontWeight={700}
                    color="primary"
                    mb={2}
                >
                    Commercial Activation Requests ({filteredRows.length} total)
                </Typography>

                <TableContainer>

                    <Table size="small">

                        <TableHead>

                            <TableRow>

                                {columns.map((column) => (

                                    <TableCell
                                        key={column}
                                        sx={{
                                            fontWeight: 700,
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {column
                                            .replaceAll("_", " ")
                                            .toUpperCase()}
                                    </TableCell>

                                ))}

                            </TableRow>

                        </TableHead>

                        <TableBody>

                            {loading ? (

                                <TableRow>

                                    <TableCell
                                        colSpan={columns.length}
                                        align="center"
                                        sx={{
                                            py: 5,
                                        }}
                                    >
                                        <CircularProgress />
                                    </TableCell>

                                </TableRow>

                            ) : filteredRows.length === 0 ? (

                                <TableRow>

                                    <TableCell
                                        colSpan={columns.length}
                                        align="center"
                                        sx={{
                                            py: 5,
                                            color: "text.secondary",
                                        }}
                                    >
                                        No Commercial Activation Requests Found
                                    </TableCell>

                                </TableRow>

                            ) : (

                                filteredRows.map((row) => (

                                    <TableRow
                                        hover
                                        key={row.id}
                                    >
                                        <TableCell>{row.id}</TableCell>

                                        <TableCell>
                                            {row.uid || "-"}
                                        </TableCell>

                                        <TableCell>
                                            {row.imei || "-"}
                                        </TableCell>

                                        <TableCell>
                                            {row.commercial_request_source || "-"}
                                        </TableCell>

                                        <TableCell>
                                            {row.commercial_activation_period || "-"}
                                        </TableCell>

                                        <TableCell>
                                            {row.commercial_activation_status || "-"}
                                        </TableCell>

                                        <TableCell>
                                            {row.commercial_activation_start_date || "-"}
                                        </TableCell>

                                        <TableCell>
                                            {row.commercial_activation_end_date || "-"}
                                        </TableCell>


                                    </TableRow>

                                ))

                            )}

                        </TableBody>

                    </Table>

                </TableContainer>

                {/* ================= Pagination ================= */}

                <Box
                    mt={3}
                    display="flex"
                    justifyContent="center"
                >
                    <Pagination
                        page={page}
                        count={totalPages}
                        color="primary"
                        onChange={(event, value) =>
                            setPage(value)
                        }
                    />
                </Box>

            </Paper>


            {/* ================= Snackbar ================= */}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                onClose={() =>
                    setSnackbar({
                        ...snackbar,
                        open: false,
                    })
                }
            >
                <Alert
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

        </Box>
    );
};

export default CommercialActivation;